import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { gql, useQuery, useMutation } from '@apollo/client'
import { Select, Input, Button, Tag, Spin, Alert, Divider, Modal } from 'antd'

import orderActions from '../../../redux/orders/actions'
import { LEAD_SOURCES } from '../../../constants/orders'
import { companyTypeTag } from '../detail/format'
import { COMPANY_DETAIL_QUERY } from '../detail/queries'
import CompanyProjectSelector from '../../SelectProjects/CompanyProjectSelector'
import SelectCountry from '../../../components/SelectCountry'
import AddressFormContainer from '../../Customers/ShippingAddresses/AddressFormContainer'
import CompanyShippingAddressPicker from './CompanyShippingAddressPicker'
import CompanyAddOrderWrapper from './companyAddOrder.style'

const { TextArea } = Input
const { saveOrder, searchCses, searchSales } = orderActions

// The REST create-order endpoint only takes `purchase_as_company` (a boolean);
// the actual company is attached afterward via this GraphQL mutation (same one
// the inquiry page's "เลือกบริษัท" uses). Without it the order shows "บริษัท: -".
const UPDATE_COMPANY_ORDER_MUTATION = gql`
    mutation UpdateCompanyPurchaseOrder($orderId: String!, $customerCompanyId: String!) {
        updateCompanyPurchaseOrder(orderId: $orderId, customerCompanyId: $customerCompanyId) {
            errors
        }
    }
`

// Delivery modes map to the REST order `shipping_type` enum. `by_osh`/`export`
// need a destination address; the other two only leave an explanatory note.
const DELIVERY_OPTIONS = [
    { value: 'by_osh', label: 'เราจัดส่ง' },
    { value: 'self_pickup', label: 'ลูกค้ารับสินค้าเอง' },
    { value: 'not_specified_address', label: 'ไม่ระบุที่อยู่จัดส่ง' },
    { value: 'export', label: 'ส่งออก' }
]

const PAYMENT_OPTIONS = [
    { value: 'cash', label: 'เงินสด' },
    { value: 'credit_card', label: 'บัตรเครดิต' }
]

// Backend Customer#lead_source enum -> Thai display label.
const LEAD_SOURCE_LABELS = {
    web: 'เว็บไซต์',
    line: 'Line OA',
    phone: 'โทรศัพท์',
    email: 'อีเมล',
    sales_contact: 'เซลล์ติดต่อเอง',
    facebook: 'Facebook',
    web_cs: 'เว็บไซต์ (CS)',
    out_going_call: 'โทรออกหาลูกค้า'
}

const DELIVERY_NOTES = {
    self_pickup: 'ลูกค้ามารับสินค้าที่สาขาเอง — ไม่ต้องเลือกที่อยู่จัดส่ง',
    not_specified_address: 'ยังไม่ระบุที่อยู่จัดส่ง — สามารถเพิ่มภายหลังก่อนยืนยันออเดอร์'
}

// Radio "dot" row matching the design (big filled dot + label), used for the
// delivery and payment choices instead of stock antd radios.
const OptionRow = ({ selected, label, onClick }) => (
    <div className="option-row" onClick={onClick}>
        <span className={selected ? 'dot dot--on' : 'dot'} />
        <span>{label}</span>
    </div>
)

// Build staff Select options from the orders slice search results (an Immutable
// List of ids) joined against the normalized entities map.
const staffOptions = (ids, entities) =>
    (ids || []).map((id) => {
        const first = (entities && entities.getIn([`${id}`, 'first_name'])) || ''
        const last = (entities && entities.getIn([`${id}`, 'last_name'])) || ''
        return { value: id, label: `${first} ${last}`.trim() || `#${id}` }
    })

const CompanyAddOrderContainer = ({ companyId }) => {
    const dispatch = useDispatch()

    const { data, loading, error } = useQuery(COMPANY_DETAIL_QUERY, {
        variables: { id: companyId }
    })

    const orders = useSelector((state) => state.get('orders'))
    const entities = useSelector((state) => state.get('Entities'))

    const [updateCompanyOrder] = useMutation(UPDATE_COMPANY_ORDER_MUTATION)

    const [form, setForm] = useState({
        contactNo: null,
        leadSource: null,
        note: '',
        delivery: 'by_osh',
        shippingAddressId: null,
        countryId: null,
        csId: null,
        saleId: null,
        payment: 'cash',
        projectIds: undefined
    })
    const [localError, setLocalError] = useState(null)
    // Add-shipping-address modal + a toggle that tells the address list to refetch
    // after a new address is saved.
    const [showAddrModal, setShowAddrModal] = useState(false)
    const [refetchAddr, setRefetchAddr] = useState(false)

    const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

    const company = data && data.company
    const contacts = (company && company.contacts && company.contacts.nodes) || []
    const typeTag = useMemo(() => company && companyTypeTag(company.companyType), [company])

    const contactOptions = contacts.map((c) => ({
        value: c.customerNo,
        label: c.fullName || c.customerNo
    }))

    const csOptions = staffOptions(
        orders.get('csItems') && orders.get('csItems').toJS(),
        entities.get('customerServices')
    )
    const saleOptions = staffOptions(
        orders.get('saleItems') && orders.get('saleItems').toJS(),
        entities.get('sales')
    )

    const saving = orders.get('saving')
    const showAddresses = form.delivery === 'by_osh'
    const showCountry = form.delivery === 'export'
    const deliveryNote = DELIVERY_NOTES[form.delivery]

    const handleSubmit = () => {
        if (!form.contactNo) {
            setLocalError('กรุณาเลือกผู้ติดต่อผู้สั่งซื้อ')
            return
        }
        if (form.delivery === 'by_osh' && !form.shippingAddressId) {
            setLocalError('กรุณาเลือกที่อยู่จัดส่ง')
            return
        }
        setLocalError(null)

        const order = {
            purchase_as_company: true,
            lead_source: form.leadSource,
            note: form.note,
            shipping_type: form.delivery,
            payment_method: form.payment,
            cs_id: form.csId,
            sale_id: form.saleId,
            company_project_ids: form.projectIds || []
        }
        if (form.delivery === 'by_osh') order.shipping_address_id = form.shippingAddressId
        if (form.delivery === 'export') {
            order.order_export_shipment_attributes = { country_id: form.countryId }
        }

        // Create the order, then attach this company to it before landing on the
        // inquiry. We suppress the saga's own redirect so the company is assigned
        // first (otherwise the inquiry loads with "บริษัท: -").
        dispatch(saveOrder(order, form.contactNo, {
            redirectToInquiryPage: false,
            onSuccess: ({ orderId }) => {
                const goToInquiry = () => location.replace(`/inquiries/${orderId}`)
                updateCompanyOrder({
                    variables: { orderId: `${orderId}`, customerCompanyId: `${company.id}` }
                }).then(goToInquiry).catch(goToInquiry)
            }
        }))
    }

    if (loading) return <Spin style={{ display: 'block', margin: '80px auto' }} />
    if (error) return <Alert type="error" message={error.message} />
    if (!company) return <Alert type="warning" message="ไม่พบข้อมูลบริษัท" />

    return (
        <CompanyAddOrderWrapper>
            <div className="card">
                {/* header */}
                <div className="section header">
                    <div className="header-identity">
                        <div className="header-title-row">
                            <span className="header-title">เพิ่มออเดอร์ — {company.name}</span>
                            {typeTag && <Tag color={typeTag.color} style={{ margin: 0 }}>{typeTag.label}</Tag>}
                        </div>
                        {company.taxId && (
                            <span className="header-meta">
                                เลขประจำตัวผู้เสียภาษี: <span className="num">{company.taxId}</span>
                            </span>
                        )}
                    </div>
                </div>

                {/* contact + source + memo */}
                <div className="section">
                    <div className="field-grid">
                        <div className="field">
                            <span className="field-label">ผู้ติดต่อผู้สั่งซื้อ</span>
                            <Select
                                style={{ width: '100%' }}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder="เลือกผู้ติดต่อ"
                                value={form.contactNo}
                                onChange={(v) => set('contactNo', v || null)}
                                options={contactOptions}
                            />
                        </div>
                        <div className="field">
                            <span className="field-label">แหล่งที่มาลูกค้า</span>
                            <Select
                                style={{ width: '100%' }}
                                allowClear
                                placeholder="เลือกแหล่งที่มาลูกค้า"
                                value={form.leadSource}
                                onChange={(v) => set('leadSource', v || null)}
                                options={LEAD_SOURCES.map((s) => ({ value: s, label: LEAD_SOURCE_LABELS[s] || s }))}
                            />
                        </div>
                    </div>
                    <div className="field">
                        <span className="field-label">บันทึกช่วยจำ</span>
                        <TextArea
                            rows={3}
                            placeholder="รายละเอียดเพิ่มเติมของออเดอร์นี้"
                            value={form.note}
                            onChange={(e) => set('note', e.target.value)}
                        />
                    </div>
                </div>

                {/* delivery */}
                <div className="section">
                    <span className="section-title">การจัดส่ง</span>
                    <div className="option-row-group">
                        {DELIVERY_OPTIONS.map((o) => (
                            <OptionRow
                                key={o.value}
                                label={o.label}
                                selected={form.delivery === o.value}
                                onClick={() => set('delivery', o.value)}
                            />
                        ))}
                    </div>

                    {showAddresses && (
                        <div className="delivery-detail">
                            <div className="delivery-actions">
                                <span className="field-label">เลือกที่อยู่จัดส่ง</span>
                                <Button
                                    size="small"
                                    disabled={!form.contactNo}
                                    onClick={() => { setRefetchAddr(false); setShowAddrModal(true) }}>
                                    + เพิ่มที่อยู่
                                </Button>
                                <CompanyProjectSelector
                                    mode="multiple"
                                    style={{ minWidth: 240 }}
                                    value={form.projectIds}
                                    onChange={(v) => set('projectIds', v)}
                                />
                            </div>
                            {form.contactNo ? (
                                <CompanyShippingAddressPicker
                                    key={form.contactNo}
                                    customerId={form.contactNo}
                                    value={form.shippingAddressId}
                                    onChange={(v) => set('shippingAddressId', v)}
                                    refetch={refetchAddr}
                                    setRefetch={setRefetchAddr}
                                />
                            ) : (
                                <div className="note-line">เลือกผู้ติดต่อผู้สั่งซื้อก่อน จึงจะแสดงที่อยู่จัดส่งได้</div>
                            )}
                        </div>
                    )}

                    {showCountry && (
                        <div className="delivery-detail">
                            <span className="field-label">ประเทศปลายทาง</span>
                            <SelectCountry
                                defaultValue={form.countryId}
                                onChange={(v) => set('countryId', v)}
                            />
                        </div>
                    )}

                    {deliveryNote && <div className="note-line">{deliveryNote}</div>}
                </div>

                {/* staff + payment */}
                <div className="section">
                    <div className="field-grid">
                        <div className="field">
                            <span className="field-label">ผู้ดูแล</span>
                            <Select
                                style={{ width: '100%' }}
                                showSearch
                                allowClear
                                placeholder="ค้นหาผู้ดูแล (CS)"
                                filterOption={false}
                                notFoundContent={orders.get('csesLoading') ? <Spin size="small" /> : null}
                                onSearch={(q) => q && dispatch(searchCses(q))}
                                value={form.csId}
                                onChange={(v) => set('csId', v || null)}
                                options={csOptions}
                            />
                        </div>
                        <div className="field">
                            <span className="field-label">พนักงานขาย</span>
                            <Select
                                style={{ width: '100%' }}
                                showSearch
                                allowClear
                                placeholder="ค้นหาพนักงานขาย"
                                filterOption={false}
                                notFoundContent={orders.get('salesLoading') ? <Spin size="small" /> : null}
                                onSearch={(q) => q && dispatch(searchSales(q))}
                                value={form.saleId}
                                onChange={(v) => set('saleId', v || null)}
                                options={saleOptions}
                            />
                        </div>
                    </div>
                    <div className="field">
                        <span className="field-label">วิธีการชำระเงิน</span>
                        <div className="option-row-group">
                            {PAYMENT_OPTIONS.map((o) => (
                                <OptionRow
                                    key={o.value}
                                    label={o.label}
                                    selected={form.payment === o.value}
                                    onClick={() => set('payment', o.value)}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* footer */}
                <div className="footer">
                    <Button type="primary" size="large" loading={saving} onClick={handleSubmit}>
                        เพิ่มออเดอร์
                    </Button>
                    <Link className="cancel-link" to={`/companies/${companyId}`}>ยกเลิก</Link>
                    {localError && (
                        <>
                            <Divider type="vertical" />
                            <span style={{ color: '#f5222d', fontSize: 13 }}>{localError}</span>
                        </>
                    )}
                </div>
            </div>

            <Modal
                open={showAddrModal}
                footer={null}
                width="65%"
                destroyOnClose
                onCancel={() => setShowAddrModal(false)}>
                <h2>เพิ่มที่อยู่จัดส่งของลูกค้า</h2>
                {form.contactNo && (
                    <AddressFormContainer
                        customerId={form.contactNo}
                        showSubmitBtn
                        onSuccess={() => { setRefetchAddr(true); setShowAddrModal(false) }}
                    />
                )}
            </Modal>
        </CompanyAddOrderWrapper>
    )
}

export default CompanyAddOrderContainer
